import {
  DestroyRef,
  Directive,
  effect,
  input,
  signal,
  TemplateRef,
  untracked,
  ViewContainerRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { UserState } from './user.state';
import { PermissionsService } from './permissions.service';

interface Permissions {
  allowedResources?: PermissionResource[];
  allowedActions?: PermissionAction[];
  directAllowAccess?: boolean;
}

@Directive({
  selector: '[coinPermissions]',
  standalone: true
})
export class PermissionsDirective {
  private readonly userState = inject(UserState);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionsService = inject(PermissionsService);

  coinPermissions = input<Permissions>(undefined);

  private readonly hasPermission = signal(false);

  constructor() {
    const templateRef = inject<TemplateRef<unknown>>(TemplateRef);
    const viewContainer = inject(ViewContainerRef);

    effect(() => {
      if (this.hasPermission()) {
        if (!viewContainer.length) {
          viewContainer.createEmbeddedView(templateRef);
        }
      } else {
        viewContainer.clear();
      }
    });

    effect(() => this.updateVisibilityBasedOnPermissions(this.coinPermissions()));
  }

  private updateVisibilityBasedOnPermissions(requiredPermissions: Permissions): void {
    // allPermissions$ reads the permissions signal on subscribe, the calling effect must only depend on the input
    untracked(() =>
      this.userState.allPermissions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(userPermissions => {
        if (requiredPermissions.directAllowAccess) {
          this.hasPermission.set(true);
        } else if (userPermissions && requiredPermissions.allowedResources?.length) {
          const allowedActions = requiredPermissions.allowedActions || [PermissionAction.All, PermissionAction.Read];
          this.hasPermission.set(
            this.permissionsService.hasPermissionsAndActions({
              resources: requiredPermissions.allowedResources,
              actions: allowedActions
            })
          );
        }
      })
    );
  }
}
